<!--
  UserEdit.svelte — Dedicated admin page for editing a single user.

  Replaces the crowded edit-user modal. Each section of user management
  gets its own Card for clarity: profile fields, roles, admin notes,
  notifications, and account toggles.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Button,
		Input,
		Spinner,
		Toggle,
		Label,
		Textarea,
		Badge,
		Avatar,
		Checkbox,
		Modal
	} from 'flowbite-svelte';
	import { push } from '../../abstract-spa-router';
	import PageHeader from '../../components/PageHeader.svelte';
	import AbstractDropdown from '../../components/AbstractDropdown.svelte';
	import RelativeTime from '../../components/RelativeTime.svelte';
	import AuditLogTable from '../../components/AuditLogTable.svelte';
	import ConfirmButton from '../../components/ConfirmButton.svelte';
	import ConfirmModal from '../../components/ConfirmModal.svelte';
	import {
		getUser,
		updateUser,
		deleteUser,
		hardDeleteUser,
		listRoles,
		getUserRoles,
		setUserRoles,
		getUserApplications,
		createUserNotification,
		listUserNotifications,
		listAuditLogs,
		revokeUserSessions,
		unregisterUserFromApplication
	} from '../../AppNetwork';
	import { toastSuccess, toastError } from '../../toast';
	import site from '../../site';
	import { t, t2 } from '../../i18n';
	import type {
		User,
		Role,
		RoleReference,
		UserApplication,
		UserNotification,
		AuditLog,
		AuditUserRef,
		SortDirection
	} from '../../AppTypes';

	
	interface Props {
		/** Route param injected by abstract-spa-router. */
		params?: { id?: string };
	}

	let { params = {} }: Props = $props();

	// ── State ─────────────────────────────────────────────────────

	/** The user being edited. */
	let user: User | null = $state(null);
	/** Whether the initial load is in progress. */
	let loading = $state(true);
	/** Error text for the initial load. */
	let error = $state('');
	/** Whether save is in progress. */
	let isSaving = $state(false);
	/** Whether session revocation is in progress. */
	let isRevoking = $state(false);

	// ── Form fields ───────────────────────────────────────────────

	let formFirstName = $state('');
	let formLastName = $state('');
	let formUsername = $state('');
	let formAbout = $state('');
	let formCompany = $state('');
	let formWebsite = $state('');
	let formLocation = $state('');
	let formNotes = $state('');
	let formIsAdmin = $state(false);
	let formIsActive = $state(true);
	let formIsVerified = $state(false);

	// ── Roles ─────────────────────────────────────────────────────

	let allRoles: Role[] = $state([]);
	let formRoleIds: number[] = $state([]);
	let rolesLoading = $state(false);

	// ── Applications ──────────────────────────────────────────────

	let userApps: UserApplication[] = $state([]);
	/** Application pending removal (for confirm modal). */
	let appToRemove: UserApplication | null = $state(null);
	/** Whether the remove-app modal is open. */
	let removeAppModalOpen = $state(false);
	/** Whether unregistering is in progress. */
	let isUnregistering = $state(false);

	// ── User Deletion ────────────────────────────────────────────

	/** Whether the soft-delete modal is open. */
	let softDeleteModalOpen = $state(false);
	/** Whether soft-delete is in progress. */
	let isSoftDeleting = $state(false);
	/** Whether the hard-delete modal is open. */
	let hardDeleteModalOpen = $state(false);
	/** Whether hard-delete is in progress. */
	let isHardDeleting = $state(false);
	/** Text the user must type to confirm hard-delete. */
	let hardDeleteConfirmText = $state('');

	/** Soft-delete the user and navigate back to the user list. */
	async function handleSoftDelete(): Promise<void> {
		if (!user) return;
		isSoftDeleting = true;
		/** Soft-delete the user via API and redirect on success. */
		try {
			await deleteUser(user.id);
			toastSuccess(t2('User deactivated.'));
			push('/Users');
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to deactivate user.'));
		} finally {
			isSoftDeleting = false;
			softDeleteModalOpen = false;
		}
	}

	/** Permanently delete the user and navigate back to the user list. */
	async function handleHardDelete(): Promise<void> {
		if (!user) return;
		isHardDeleting = true;
		/** Hard-delete the user via API and redirect on success. */
		try {
			await hardDeleteUser(user.id);
			toastSuccess(t2('User permanently deleted.'));
			push('/Users');
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to delete user.'));
		} finally {
			isHardDeleting = false;
			hardDeleteModalOpen = false;
			hardDeleteConfirmText = '';
		}
	}

	// ── Notifications ─────────────────────────────────────────────

	let notifTitle = $state('');
	let notifMessage = $state('');
	let notifType: 'info' | 'warning' | 'error' | 'success' = $state('info');
	/** Items for the notification type dropdown. */
	const notifTypeItems = [
		{ value: 'info', name: t2('Info'), icon: 'fa-solid fa-circle-info' },
		{ value: 'warning', name: t2('Warning'), icon: 'fa-solid fa-triangle-exclamation' },
		{ value: 'error', name: t2('Error'), icon: 'fa-solid fa-circle-xmark' },
		{ value: 'success', name: t2('Success'), icon: 'fa-solid fa-circle-check' }
	];
	let notifActionUrl = $state('');
	let notifSubmitting = $state(false);
	let userNotifications: UserNotification[] = $state([]);
	let notifTotal = $state(0);
	let notifsLoading = $state(false);

	// ── Audit Logs ───────────────────────────────────────────────

	let auditLogs: AuditLog[] = $state([]);
	let auditUsers: Record<number, AuditUserRef> = $state({});
	let auditTotal = $state(0);
	let auditPage = $state(1);
	let auditLoading = $state(false);
	const auditLimit = 15;
	/** Active server-side sort key for the audit table (`''` = newest first). */
	let auditSortBy = $state('');
	/** Active server-side sort direction for the audit table. */
	let auditSortDir: SortDirection = $state('desc');

	// ── Data loading ──────────────────────────────────────────────

	/** Load the user and populate form fields. */
	async function loadUser(): Promise<void> {
		const id = parseInt(params.id || '', 10);
		/** Bail out early if the route param is not a valid numeric ID */
		if (isNaN(id)) {
			error = t2('Invalid user ID.');
			loading = false;
			return;
		}

		loading = true;
		error = '';
		/** Fetch the user and kick off all secondary data loads in parallel */
		try {
			user = await getUser(id);
			populateForm(user);

			/* Reset paginated sections for the new user */
			auditPage = 1;

			/* Kick off parallel secondary loads */
			await Promise.all([
				loadRolesAndAssignments(user),
				loadUserApps(user.id),
				loadNotifData(user.id),
				loadAuditData(user.id)
			]);
		} catch (e) {
			error = e instanceof Error ? e.message : t2('Failed to load user');
		} finally {
			loading = false;
		}
	}

	/** Set form fields from user entity. */
	function populateForm(u: User): void {
		formFirstName = u.firstName || '';
		formLastName = u.lastName || '';
		formUsername = u.username || '';
		formAbout = u.about || '';
		formCompany = u.company || '';
		formWebsite = u.website || '';
		formLocation = u.location || '';
		formNotes = u.notes || '';
		formIsAdmin = u.isAdmin;
		formIsActive = u.isActive;
		formIsVerified = u.isVerified;
	}

	/** Load all roles, then the user's current assignments. */
	async function loadRolesAndAssignments(u: User): Promise<void> {
		rolesLoading = true;
		/** Fetch all available roles, then resolve which ones the user has */
		try {
			allRoles = await listRoles();
			const userRoles: RoleReference[] = await getUserRoles(u.id);
			formRoleIds = allRoles
				.filter((r) => userRoles.some((ur) => ur.roleUUID === r.roleUUID || ur.name === r.name))
				.map((r) => r.id);
		} catch {
			formRoleIds = [];
		} finally {
			rolesLoading = false;
		}
	}

	/** Load the user's registered applications. */
	async function loadUserApps(userId: number): Promise<void> {
		try {
			userApps = await getUserApplications(userId);
		} catch {
			userApps = [];
		}
	}

	/** Unregister the user from an application. */
	async function handleUnregisterApp(): Promise<void> {
		if (!user || !appToRemove) return;
		isUnregistering = true;
		/** Remove the user's registration from the application and refresh the list. */
		try {
			await unregisterUserFromApplication(appToRemove.applicationID, user.id);
			toastSuccess(t2('Application access removed.'));
			removeAppModalOpen = false;
			appToRemove = null;
			await loadUserApps(user.id);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to remove application'));
		} finally {
			isUnregistering = false;
		}
	}

	/** Open the confirm modal for removing an application. */
	function confirmRemoveApp(ua: UserApplication): void {
		appToRemove = ua;
		removeAppModalOpen = true;
	}

	/** Load recent notifications. */
	async function loadNotifData(userId: number): Promise<void> {
		notifsLoading = true;
		userNotifications = [];
		notifTotal = 0;
		notifTitle = '';
		notifMessage = '';
		notifType = 'info';
		notifActionUrl = '';
		/** Fetch the most recent notifications for this user */
		try {
			const res = await listUserNotifications(userId, { limit: 5 });
			userNotifications = res.items;
			notifTotal = res.total;
		} catch {
			/* swallow */
		} finally {
			notifsLoading = false;
		}
	}

	/** Load audit logs where this user is source or target. */
	async function loadAuditData(userId: number): Promise<void> {
		auditLoading = true;
		/** Fetch the current page of audit logs related to this user */
		try {
			const offset = (auditPage - 1) * auditLimit;
			const result = await listAuditLogs({
				relatedUserId: userId,
				limit: auditLimit,
				offset,
				sortBy: auditSortBy || undefined,
				sortDir: auditSortBy ? auditSortDir : undefined
			});
			auditLogs = result.items || [];
			auditTotal = result.total || 0;
			auditUsers = result.users || {};
		} catch {
			/* swallow */
		} finally {
			auditLoading = false;
		}
	}

	/** Handle page change from AuditLogTable. */
	function handleAuditPageChange(newPage: number): void {
		auditPage = newPage;
		if (user) loadAuditData(user.id);
	}

	/** Handle a sort change from AuditLogTable — resets to the first page. */
	function handleAuditSortChange(key: string, dir: SortDirection): void {
		auditSortBy = key;
		auditSortDir = dir;
		auditPage = 1;
		if (user) loadAuditData(user.id);
	}

	/** Send a notification. */
	async function handleSendNotification(): Promise<void> {
		if (!user || !notifTitle.trim()) return;
		notifSubmitting = true;
		/** Send the notification to the user and refresh the list on success */
		try {
			await createUserNotification(user.id, {
				title: notifTitle.trim(),
				message: notifMessage.trim() || undefined,
				notificationType: notifType,
				actionUrl: notifActionUrl.trim() || undefined
			});
			toastSuccess(t2('Notification sent to {{email}}.', { email: user.email }));
			notifTitle = '';
			notifMessage = '';
			notifType = 'info';
			notifActionUrl = '';
			await loadNotifData(user.id);
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to send notification'));
		}
		notifSubmitting = false;
	}

	// ── Save ──────────────────────────────────────────────────────

	/** Save the user's profile + role assignments. */
	async function handleSave(): Promise<void> {
		if (!user) return;
		isSaving = true;
		/** Persist updated profile fields and sync role assignments */
		try {
			await updateUser(user.id, {
				firstName: formFirstName || undefined,
				lastName: formLastName || undefined,
				username: formUsername || undefined,
				about: formAbout || undefined,
				company: formCompany || undefined,
				website: formWebsite || undefined,
				location: formLocation || undefined,
				notes: formNotes || undefined,
				isAdmin: formIsAdmin,
				isActive: formIsActive,
				isVerified: formIsVerified
			});
			await setUserRoles(user.id, formRoleIds);
			toastSuccess(t2('User updated successfully.'));
			push('/Users');
		} catch (e) {
			toastError(e instanceof Error ? e.message : t2('Failed to update user'));
		}
		isSaving = false;
	}

	// ── Helpers ───────────────────────────────────────────────────

	/** Format a date string for display. */
	function formatDate(dateString: string): string {
		return new Date(dateString).toLocaleDateString(undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit'
		});
	}

	onMount(loadUser);
</script>

<PageHeader title={$t("Edit User") + (user ? ` \u2014 ${[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || user.username || $t('Unnamed')}` : '')} description="" />

{#if loading}
	<div class="flex justify-center py-12">
		<Spinner size="8" />
	</div>
{:else if error}
	<div class="py-12 text-center">
		<i class="fa-solid fa-exclamation-triangle mb-4 text-4xl text-red-500"></i>
		<p class="text-gray-500 dark:text-gray-400">{error}</p>
		<Button class="mt-4" onclick={() => push('/Users')}>{$t("Back to Users")}</Button>
	</div>
{:else if user}
	<!-- ═══ HERO HEADER ═══════════════════════════════════════════ -->
	<div class="relative mb-8 overflow-hidden rounded-2xl">
		<!-- Gradient background -->
		<div
			class="absolute inset-0 bg-gradient-to-r from-primary-600 to-primary-800 dark:from-primary-900 dark:to-gray-900"
		></div>
		<div
			class="absolute inset-0 bg-repeat opacity-10"
			style="background-image: url('/AbstractID-Tile-Dark.png'); background-size: 80px"
		></div>

		<div class="relative px-6 py-8 sm:px-8">
			<div class="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
				<!-- Avatar -->
				<Avatar
					src={user.imageName ? `${site.backend.baseURL}/uploads/avatars/${user.imageName}` : ''}
					size="xl"
					class="shrink-0 rounded-full ring-4 ring-white/20"
				/>

				<!-- Name + meta -->
				<div class="min-w-0 flex-1">
					<h1 class="text-2xl font-bold leading-tight text-white sm:text-3xl">
						{[user.firstName, user.lastName].filter(Boolean).join(' ') || user.email || user.username || $t('Unnamed User')}
					</h1>
					<div class="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
						<span class="text-sm text-primary-100">{user.email}</span>
						{#if user.username}
							<span class="text-sm text-primary-200/70">@{user.username}</span>
						{/if}
					</div>

					<!-- Badges -->
					<div class="mt-3 flex flex-wrap gap-1.5">
						{#if user.isAdmin}
							<Badge color="green" class="border border-green-400/30 !bg-green-500/20 !text-green-200"
								>{$t("Admin")}</Badge
							>
						{:else}
							<Badge color="gray" class="border border-white/20 !bg-white/10 !text-white/80">{$t("User")}</Badge>
						{/if}
						{#if user.authProvider === 'imported'}
							<Badge color="purple" class="border border-purple-400/30 !bg-purple-500/20 !text-purple-200"
								>{$t("Imported")}</Badge
							>
						{/if}
						{#if !user.isActive && user.authProvider !== 'imported'}
							<Badge color="red" class="border border-red-400/30 !bg-red-500/20 !text-red-200"
								>{$t("Inactive")}</Badge
							>
						{/if}
						{#if !user.isVerified}
							<Badge color="yellow" class="border border-yellow-400/30 !bg-yellow-500/20 !text-yellow-200"
								>{$t("Unverified")}</Badge
							>
						{/if}
						{#if user.passwordless}
							<Badge color="pink" class="border border-pink-400/30 !bg-pink-500/20 !text-pink-200"
								>{$t("Passwordless")}</Badge
							>
						{/if}
					</div>
				</div>

				<!-- Quick stats -->
				<div class="hidden shrink-0 flex-col items-end gap-1 text-right sm:flex">
					<div class="space-y-0.5 text-xs text-primary-200/60">
						<p>ID <span class="font-mono text-primary-100">{user.id}</span></p>
						<p>{$t("Joined")} {formatDate(user.created)}</p>
						<p>{$t("Last login:")} {user.lastLoginDate ? formatDate(user.lastLoginDate) : $t('Never')}</p>
					</div>
					{#if user.userUUID}
						<p class="mt-1 max-w-[200px] break-all font-mono text-[10px] text-primary-300/40">
							{user.userUUID}
						</p>
					{/if}
				</div>
			</div>
		</div>
	</div>

	<!-- ═══ MAIN LAYOUT: 2-column ═════════════════════════════════ -->
	<div class="grid grid-cols-1 gap-8 xl:grid-cols-5">
		<!-- ─── LEFT: Profile + Roles + Notes (wider) ──────────── -->
		<div class="space-y-8 xl:col-span-3">
			<!-- Personal Information -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-user text-primary-500"></i>
					{$t("Personal Information")}
				</h3>
				<div class="widget p-6">
					<div class="space-y-5">
						<div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
							<div>
								<Label class="mb-1.5 text-sm font-medium">{$t("First Name")}</Label>
								<Input bind:value={formFirstName} placeholder={$t("First name")} />
							</div>
							<div>
								<Label class="mb-1.5 text-sm font-medium">{$t("Last Name")}</Label>
								<Input bind:value={formLastName} placeholder={$t("Last name")} />
							</div>
						</div>
						<div class="grid grid-cols-1 gap-5 sm:grid-cols-2">
							<div>
								<Label class="mb-1.5 text-sm font-medium">{$t("Username")}</Label>
								<Input bind:value={formUsername} placeholder={$t("username")} />
							</div>
							<div>
								<Label class="mb-1.5 text-sm font-medium">{$t("Email")}</Label>
								<Input value={user.email || ''} disabled class="opacity-60" />
							</div>
						</div>
						<div>
							<Label class="mb-1.5 text-sm font-medium">{$t("About")}</Label>
							<Textarea bind:value={formAbout} rows={3} placeholder={$t("Bio or description...")} class="w-full" />
						</div>
						<div class="grid grid-cols-1 gap-5 sm:grid-cols-3">
							<div>
								<Label class="mb-1.5 text-sm font-medium">{$t("Company")}</Label>
								<Input bind:value={formCompany} placeholder={$t("Company")} />
							</div>
							<div>
								<Label class="mb-1.5 text-sm font-medium">{$t("Location")}</Label>
								<Input bind:value={formLocation} placeholder={$t("City, Country")} />
							</div>
							<div>
								<Label class="mb-1.5 text-sm font-medium">{$t("Website")}</Label>
								<Input bind:value={formWebsite} placeholder="https://..." />
							</div>
						</div>
					</div>
				</div>
			</section>

			<!-- Roles -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-shield-halved text-primary-500"></i>
					{$t("Roles")}
				</h3>
				<div class="widget p-6">
					{#if rolesLoading}
						<div class="flex items-center gap-2 py-4">
							<Spinner size="4" />
							<span class="text-sm text-gray-400">{$t("Loading roles...")}</span>
						</div>
					{:else if allRoles.length === 0}
						<p class="py-4 text-sm text-gray-400">{$t("No roles defined yet.")}</p>
					{:else}
						<div class="flex flex-wrap gap-2">
							{#each allRoles as role (role.id)}
								<label
									class="flex cursor-pointer items-center gap-1.5 rounded-full px-3 py-1.5 text-sm transition-all
									{formRoleIds.includes(role.id)
										? 'bg-primary-100 text-primary-700 shadow-sm ring-1 ring-primary-300 dark:bg-primary-900/40 dark:text-primary-300 dark:ring-primary-600'
										: 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'}"
								>
									<Checkbox
										checked={formRoleIds.includes(role.id)}
										onchange={() => {
											if (formRoleIds.includes(role.id)) {
												formRoleIds = formRoleIds.filter((id) => id !== role.id);
											} else {
												formRoleIds = [...formRoleIds, role.id];
											}
										}}
										class="h-3.5 w-3.5"
									/>
									{role.name}
								</label>
							{/each}
						</div>
					{/if}
				</div>
			</section>

			<!-- Admin Notes -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-note-sticky text-primary-500"></i>
					{$t("Admin Notes")}
				</h3>
				<div class="widget p-6">
					<Textarea
						bind:value={formNotes}
						rows={4}
						placeholder={$t("Internal notes (only visible to admins)...")}
						class="w-full"
					/>
				</div>
			</section>

			<!-- Audit Trail -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-clock-rotate-left text-primary-500"></i>
					{$t("Audit Trail")}
					{#if auditTotal > 0}
						<Badge color="gray" class="ml-1">{auditTotal}</Badge>
					{/if}
				</h3>
				<div
					class="widget overflow-hidden !p-0"
				>
					<AuditLogTable
						logs={auditLogs}
						users={auditUsers}
						total={auditTotal}
						limit={auditLimit}
						page={auditPage}
						loading={auditLoading}
						showDetails={false}
						sortBy={auditSortBy}
						sortDir={auditSortDir}
						onpagechange={handleAuditPageChange}
						onsortchange={handleAuditSortChange}
					/>
				</div>
			</section>
		</div>

		<!-- ─── RIGHT: Account Controls + Notifications ─────── -->
		<div class="space-y-8 xl:col-span-2">
			<!-- Account Status -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-toggle-on text-primary-500"></i>
					{$t("Account Status")}
				</h3>
				<div class="widget p-6">
					<div class="space-y-4">
						<Toggle bind:checked={formIsAdmin}>{$t("Administrator")}</Toggle>
						<Toggle bind:checked={formIsActive}>{$t("Active")}</Toggle>
						<Toggle bind:checked={formIsVerified}>{$t("Verified")}</Toggle>
					</div>
					{#if user.passwordless}
						<div
							class="mt-4 flex items-center gap-2 border-t border-gray-200 pt-4 text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400"
						>
							<i class="fa-solid fa-key"></i>
							<span
								>{$t("No password — signs in via email OTP")}{user.authProvider === 'google'
									? ' ' + $t('or Google')
									: user.authProvider === 'apple'
										? ' ' + $t('or Apple')
										: user.authProvider === 'microsoft'
											? ' ' + $t('or Microsoft')
											: ''}.</span
							>
						</div>
					{/if}
					<div class="mt-4 border-t border-gray-200 pt-4 dark:border-gray-700">
						<ConfirmButton
							action={$t("revoke all sessions")}
							confirmText={$t("This will immediately invalidate all active sessions for {{name}}. They will need to sign in again.", { name: user.firstName || user.email })}
							color="red"
							outline
							size="sm"
							disabled={isRevoking}
							onconfirm={async () => {
								if (!user) return;
								isRevoking = true;
								/** Revoke all active sessions for this user */
								try {
									await revokeUserSessions(user.id);
									toastSuccess(t2('All sessions revoked.'));
								} catch {
									toastError(t2('Failed to revoke sessions.'));
								} finally {
									isRevoking = false;
								}
							}}
						>
							<i class="fa-solid fa-right-from-bracket me-1"></i>
							{isRevoking ? $t('Revoking…') : $t('Revoke All Sessions')}
						</ConfirmButton>
					</div>
				</div>
			</section>

			<!-- Applications -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-cubes text-primary-500"></i>
					{$t("Applications")}
				</h3>
				<div class="widget p-6">
					{#if userApps.length === 0}
						<p class="text-sm text-gray-400 dark:text-gray-500">{$t("No applications registered.")}</p>
					{:else}
						<div class="flex flex-wrap gap-2">
							{#each userApps as ua (ua.applicationID)}
								{#if ua.application}
									<span class="inline-flex items-center gap-1 rounded-full bg-blue-100 px-3 py-1 text-sm font-medium text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
										{ua.application.applicationName}
										<button
											type="button"
											class="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full text-blue-400 hover:bg-blue-200 hover:text-blue-600 dark:hover:bg-blue-800 dark:hover:text-blue-200"
											onclick={() => confirmRemoveApp(ua)}
											title={$t("Remove access")}
										>
											<i class="fa-solid fa-xmark text-xs"></i>
										</button>
									</span>
								{/if}
							{/each}
						</div>
					{/if}
				</div>
			</section>

			<!-- Notifications -->
			<section>
				<h3 class="mb-4 flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
					<i class="fa-solid fa-bell text-primary-500"></i>
					{$t("Notifications")}
					{#if notifTotal > 0}
						<Badge color="blue" class="ml-1">{notifTotal}</Badge>
					{/if}
				</h3>
				<div class="widget p-6">
					{#if notifsLoading && userNotifications.length === 0}
						<div class="flex items-center gap-2 py-4">
							<Spinner size="4" />
							<span class="text-sm text-gray-400">{$t("Loading notifications...")}</span>
						</div>
					{:else}
						<!-- Send Notification Form -->
						<div
							class="mb-5 rounded-xl border border-gray-200 bg-depth p-4 dark:border-gray-600"
						>
							<p class="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
								<i class="fa-solid fa-paper-plane me-1 text-primary-500"></i>{$t("Send Notification")}
							</p>
							<div class="space-y-3">
								<AbstractDropdown items={notifTypeItems} value={notifType} onchange={(v) => { notifType = v as 'info' | 'warning' | 'error' | 'success'; }} size="sm" />
								<Input size="sm" bind:value={notifTitle} placeholder={$t("Title (required)")} />
								<Input size="sm" bind:value={notifMessage} placeholder={$t("Message (optional)")} />
								<Input size="sm" bind:value={notifActionUrl} placeholder={$t("Action URL (optional)")} />
							</div>
							<div class="mt-3 flex justify-end">
								<Button
									size="xs"
									onclick={handleSendNotification}
									disabled={notifSubmitting || !notifTitle.trim()}
								>
									{#if notifSubmitting}<Spinner size="4" class="me-1" />{/if}
									{$t("Send")}
								</Button>
							</div>
						</div>

						<!-- Recent Notifications -->
						{#if userNotifications.length > 0}
							<div class="space-y-2">
								{#each userNotifications as n (n.id)}
									<div
										class="flex items-start gap-3 rounded-lg p-3 {n.isRead
											? 'opacity-60'
											: ''} border border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-700/20"
									>
										<div class="mt-0.5 shrink-0">
											<i
												class="fa-solid {n.notificationType === 'error'
													? 'fa-circle-xmark text-red-500'
													: n.notificationType === 'warning'
														? 'fa-triangle-exclamation text-yellow-500'
														: n.notificationType === 'success'
															? 'fa-circle-check text-green-500'
															: 'fa-circle-info text-blue-500'}"
											></i>
										</div>
										<div class="min-w-0 flex-1">
											<p class="text-sm font-medium text-gray-900 dark:text-white">{n.title}</p>
											<p class="mt-0.5 text-xs text-gray-400 dark:text-gray-500">
												<RelativeTime date={n.created} />
											</p>
										</div>
										{#if !n.isRead}
											<span class="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-blue-500"></span>
										{/if}
									</div>
								{/each}
							</div>
						{:else}
							<p class="py-2 text-center text-sm text-gray-400 dark:text-gray-500">
								{$t("No notifications yet.")}
							</p>
						{/if}
					{/if}
				</div>
			</section>
		</div>
	</div>

	<!-- Sticky Save Bar -->
	<div
		class="sticky bottom-4 z-10 mt-8 flex items-center justify-between gap-3 rounded-xl border border-gray-200 bg-zinc-100/90 px-6 py-4 shadow-lg backdrop-blur-sm dark:border-gray-700 dark:bg-zinc-800/90"
	>
		<div class="flex gap-2">
			<Button color="red" outline onclick={() => { softDeleteModalOpen = true; }}>
				<i class="fa-solid fa-trash mr-2"></i>{$t("Deactivate")}
			</Button>
			<Button color="red" onclick={() => { hardDeleteModalOpen = true; hardDeleteConfirmText = ''; }}>
				<i class="fa-solid fa-skull-crossbones mr-2"></i>{$t("Delete Permanently")}
			</Button>
		</div>
		<div class="flex gap-3">
			<Button color="alternative" onclick={() => push('/Users')}>{$t("Cancel")}</Button>
			<Button color="primary" onclick={handleSave} disabled={isSaving}>
				{#if isSaving}<Spinner size="4" class="mr-2" />{/if}
				{$t("Save Changes")}
			</Button>
		</div>
	</div>
{/if}

<!-- Remove Application Confirmation Modal -->
<ConfirmModal
	bind:open={removeAppModalOpen}
	title="Remove Application Access"
	message="Are you sure you want to remove access to {appToRemove?.application?.applicationName ?? 'this application'}? The user will need to re-authorize to regain access."
	confirmLabel="Remove Access"
	confirmColor="red"
	loading={isUnregistering}
	onconfirm={handleUnregisterApp}
	oncancel={() => { appToRemove = null; }}
/>

<!-- Soft-Delete Confirmation Modal -->
<ConfirmModal
	bind:open={softDeleteModalOpen}
	title="Deactivate User"
	message="This will soft-delete the user. Their data is preserved but they will be excluded from all queries and cannot log in. Continue?"
	confirmLabel="Deactivate"
	confirmColor="red"
	loading={isSoftDeleting}
	onconfirm={handleSoftDelete}
/>

<!-- Hard-Delete Confirmation Modal (requires typing DELETE) -->
<Modal bind:open={hardDeleteModalOpen} title={$t("Permanently Delete User")} size="sm" autoclose={false}>
	<div class="text-center">
		<i class="fa-solid fa-skull-crossbones mx-auto mb-4 text-5xl text-red-500"></i>
		<h3 class="mb-2 text-lg font-bold text-gray-900 dark:text-white">
			{$t("This action is irreversible")}
		</h3>
		<p class="mb-4 text-sm text-gray-500 dark:text-gray-400">
			{$t("All data associated with")} <strong>{user?.email}</strong> {$t("will be permanently removed from the database. Audit log entries will be preserved with the user reference cleared.")}
		</p>
		<p class="mb-3 text-sm font-medium text-gray-700 dark:text-gray-300">
			{$t("Type")} <code class="rounded bg-red-100 px-1.5 py-0.5 font-bold text-red-700 dark:bg-red-900/30 dark:text-red-400">DELETE</code> {$t("to confirm:")}
		</p>
		<Input
			bind:value={hardDeleteConfirmText}
			placeholder="DELETE"
			class="mb-4 text-center font-mono"
		/>
		<div class="flex justify-center gap-4">
			<Button color="alternative" onclick={() => { hardDeleteModalOpen = false; hardDeleteConfirmText = ''; }}>{$t("Cancel")}</Button>
			<Button
				color="red"
				disabled={hardDeleteConfirmText !== 'DELETE' || isHardDeleting}
				onclick={handleHardDelete}
			>
				{#if isHardDeleting}<Spinner size="4" class="mr-2" />{/if}
				{$t("Delete Permanently")}
			</Button>
		</div>
	</div>
</Modal>
